import { useCallback, useEffect, useRef } from 'react'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setChatMessages, setLastUpdatedAt, setUnreadCount } from 'actions/supportAssistant/supportAssistantActions'
import { getLastUpdatedAt } from 'api/supportAssistant'
import { calculateUnreadCount } from 'supportAssistant/utils/unreadCountUtils'
import { get } from 'lib/storage/isomorphicLocalStorage'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { mapChatMessage } from 'api/mappers/supportAssistant/supportAssistant'
import useTabVisibility from 'hooks/useTabVisibility'

const POLLING_INTERVAL = 15000
const LAST_SEEN_PREFIX = 'lastSeenAt-'

export function useSimplePolling() {
  const dispatch = useAppDispatch()
  const isActiveRef = useRef(true)
  const abortControllerRef = useRef<AbortController | undefined>(undefined)
  const isAuthExpiredRef = useRef(false)
  const isLESAConditionalBubbleEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.opexLESAConditionalBubble)
  const isTabVisible = useTabVisibility()

  const checkForUpdates = useCallback(async() => {
    if (!isActiveRef.current || !isLESAConditionalBubbleEnabled || !isTabVisible || isAuthExpiredRef.current) {
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const response = await getLastUpdatedAt(controller.signal)

      if (!response) {
        return
      }

      const { chatId, messages, updatedAtTimestamp } = response

      dispatch(setLastUpdatedAt(updatedAtTimestamp))

      if (chatId && messages) {
        const mappedMessages = messages.map(mapChatMessage)
        dispatch(setChatMessages(chatId, mappedMessages))

        const lastSeenAt = get(`${LAST_SEEN_PREFIX}${chatId}`)
        const unreadCount = calculateUnreadCount(lastSeenAt, mappedMessages)
        dispatch(setUnreadCount(unreadCount))
      } else {
        dispatch(setUnreadCount(0))
      }
    } catch(err) {
      if (err instanceof Error) {
        if (err.name === 'AbortError') {
          return
        }
      }
      // disable polling on 401 to avoid unnecessary requests when auth has expired
      if (err?.status === 401) {
        isAuthExpiredRef.current = true
      }
    } finally {
      abortControllerRef.current = undefined
    }
  }, [dispatch, isLESAConditionalBubbleEnabled, isTabVisible])

  useEffect(() => {
    isActiveRef.current = true

    checkForUpdates()

    const intervalId = setInterval(() => {
      checkForUpdates()
    }, POLLING_INTERVAL)

    return () => {
      isActiveRef.current = false
      clearInterval(intervalId)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [checkForUpdates])

  return null
}
